import {
    OTHER_ALL_SHIELD_TAGS_QUERY,
    OTHER_PLAYER_SHIELD_TAGS_QUERY,
} from "@/graphql/queries/profileQueries";
import { classNamesExtended } from "@/helpers/common/classNames";
import { formatNumber } from "@/helpers/common/formatNumber";
import { useIsMobile } from "@/hooks/general/useIsMobile";
import { convertLetters } from "@/utils/letterConversion";
import { useQuery } from "@apollo/client";
import { UUID } from "crypto";
import React, { useState } from "react";
import TagInfoModal from "./TagInfoModal"; // adjust path

interface PlayerTag {
    id: string;
    tagTier: {
        tag: string;
        tier: number;
        tagName: string;
        shieldValue: number;
        subtitle: string;
        unitName: string;
    };
    createdAt: string;
    updatedAt: string;
    imageUrl: string;
}

interface PlayerShieldTagsQueryData {
    otherPlayerTags: PlayerTag[];
}

interface AllShieldTag {
    name: string;
    slug: string;
    milestones: number[];
    maxTiers: number;
    shieldValues: number[];
    imageTemplate: string;
    subtitle: string;
    unitName: string;
}

interface AllShieldTagsQueryData {
    otherAllTags: AllShieldTag[];
}

const getFallbackImageUrl = (tag: AllShieldTag): string => {
    return tag.imageTemplate.replace("{tier}", "1");
};

interface TagCardProps {
    tag: AllShieldTag;
    playerTag?: PlayerTag;
}

interface ShieldCabinetProps {
    playerId: UUID;
}

const TagCard: React.FC<TagCardProps> = ({ tag, playerTag }) => {
    const isMobile = useIsMobile();
    const [clickedFlipped, setClickedFlipped] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const flipped = clickedFlipped || hovered;

    const imageUrl = playerTag ? playerTag.imageUrl : getFallbackImageUrl(tag);
    const isUnlocked = Boolean(playerTag);
    const currentTier = playerTag?.tagTier.tier ?? 0;
    const nextMilestone = playerTag
        ? currentTier < tag.maxTiers
            ? tag.milestones[currentTier]
            : "Max Tier Reached"
        : tag.milestones[0];
    const currentShieldValue = playerTag?.tagTier.shieldValue ?? null;

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isMobile) {
            setModalOpen(true);
        } else {
            setClickedFlipped((prev) => !prev);
        }
    };

    return (
        <>
            <div
                className={classNamesExtended(
                    "border-brand relative aspect-square w-full max-w-[130px] cursor-pointer overflow-hidden rounded border-4 sm:max-w-none",
                    { "border-gray-300": !isUnlocked },
                )}
                style={{ perspective: "1000px" }}
                onMouseEnter={() => !isMobile && setHovered(true)}
                onMouseLeave={() => !isMobile && setHovered(false)}
                onClick={handleClick}
            >
                <div
                    className="relative h-full w-full transform transition-transform duration-700"
                    style={{
                        transform: flipped
                            ? "rotateY(180deg)"
                            : "rotateY(0deg)",
                        transformStyle: "preserve-3d",
                    }}
                >
                    {/* Front Side */}
                    <div
                        className="absolute inset-0 flex items-center justify-center p-2"
                        style={{ backfaceVisibility: "hidden" }}
                    >
                        <img
                            src={imageUrl}
                            alt={tag.name}
                            className={classNamesExtended(
                                "max-h-full max-w-full",
                                {
                                    grayscale: !isUnlocked,
                                },
                            )}
                        />
                    </div>

                    {/* Back Side (only on desktop) */}
                    {!isMobile && (
                        <div
                            className="bg-brand_charcoal text-brand absolute inset-0 flex flex-col items-center justify-center overflow-auto rounded p-2 text-xs sm:text-sm"
                            style={{
                                backfaceVisibility: "hidden",
                                transform: "rotateY(180deg)",
                            }}
                        >
                            <h3
                                className="font-prviking text-center text-lg font-bold sm:text-2xl"
                                style={{
                                    textDecorationLine: "underline overline",
                                    textDecorationThickness: "0.1em",
                                    textUnderlineOffset: "0.2em",
                                }}
                            >
                                {convertLetters(tag.name)}
                            </h3>
                            <h4 className="text-stats mb-2 text-center text-sm font-bold">
                                {tag.subtitle}
                            </h4>
                            <div className="space-y-1 text-left text-xs sm:text-sm">
                                {playerTag ? (
                                    <>
                                        <p>
                                            <strong>CURRENT TIER:</strong>{" "}
                                            <span className="text-stats">
                                                {formatNumber(currentTier)}
                                            </span>
                                        </p>
                                        <p>
                                            <strong>SHIELD VALUE:</strong>{" "}
                                            <span className="text-stats">
                                                {formatNumber(
                                                    currentShieldValue,
                                                )}
                                            </span>
                                        </p>
                                        {nextMilestone ===
                                        "Max Tier Reached" ? (
                                            <p>
                                                <strong>
                                                    MAX TIER REACHED
                                                </strong>
                                            </p>
                                        ) : (
                                            <p>
                                                <strong>NEXT TIER:</strong>{" "}
                                                <span className="text-stats">
                                                    {formatNumber(
                                                        nextMilestone,
                                                    )}{" "}
                                                    {tag.unitName}
                                                </span>
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p>
                                        <strong>NEXT TIER:</strong>{" "}
                                        <span className="text-stats">
                                            {formatNumber(nextMilestone)}{" "}
                                            {tag.unitName}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal version for mobile */}
            {isMobile && (
                <TagInfoModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    tag={tag}
                    playerTag={playerTag}
                />
            )}
        </>
    );
};

const OtherPlayerShieldTagsCabinet: React.FC<ShieldCabinetProps> = ({
    playerId,
}) => {
    console.debug("Fetching shield tags for player:", playerId);

    const {
        data: playerTagsData,
        loading: playerTagsLoading,
        error: playerTagsError,
    } = useQuery<PlayerShieldTagsQueryData>(OTHER_PLAYER_SHIELD_TAGS_QUERY, {
        variables: { playerId },
        skip: !playerId,
        fetchPolicy: "cache-and-network",
    });

    const {
        data: allTagsData,
        loading: allTagsLoading,
        error: allTagsError,
    } = useQuery<AllShieldTagsQueryData>(OTHER_ALL_SHIELD_TAGS_QUERY, {
        skip: !playerId,
        variables: { playerId },
        fetchPolicy: "cache-and-network",
    });

    if (
        (playerTagsLoading || allTagsLoading) &&
        (!playerTagsData || !allTagsData)
    )
        return <p>Loading...</p>;
    if ((playerTagsError || allTagsError) && playerTagsData && allTagsData) {
        console.warn("Polling failed — showing stale data.");
    }

    const playerTagsMap: Record<string, PlayerTag> = {};
    playerTagsData?.otherPlayerTags.forEach((tag) => {
        playerTagsMap[tag.tagTier.tag] = tag;
    });

    const allTags: AllShieldTag[] = allTagsData?.otherAllTags || [];

    return (
        <div className="w-full">
            <div className="grid grid-cols-2 justify-center gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {allTags.map((tag) => (
                    <TagCard
                        key={tag.slug}
                        tag={tag}
                        playerTag={playerTagsMap[tag.slug]}
                    />
                ))}
            </div>
        </div>
    );
};

export default OtherPlayerShieldTagsCabinet;
